<template>
  <v-chip
    :small="small"
    class="status-badge"
    :class="`status-badge--${type}`"
    :aria-label="ariaLabel || `Status: ${status}`"
    :data-testid="`status-badge-${type}`"
  >
    <v-icon
      v-if="showIcon"
      left
      :x-small="small"
      :small="!small"
      :aria-hidden="true"
    >
      {{ computedIcon }}
    </v-icon>
    {{ status }}
  </v-chip>
</template>

<script lang="ts">
import Vue from 'vue'

type StatusType = 'success' | 'error' | 'warning' | 'info'

interface StatusConfig {
  icon: string
}

const statusConfig: Record<StatusType, StatusConfig> = {
  success: {
    icon: 'mdi-check-circle'
  },
  error: {
    icon: 'mdi-close-circle'
  },
  warning: {
    icon: 'mdi-alert-circle'
  },
  info: {
    icon: 'mdi-information-circle'
  }
}

export default Vue.extend({
  name: 'StatusBadge',

  props: {
    /** Status text to display */
    status: {
      type: String,
      required: true
    },
    /** Status type determines colour and icon */
    type: {
      type: String as () => StatusType,
      required: true,
      validator: (value: string) => ['success', 'error', 'warning', 'info'].includes(value)
    },
    /** Display small variant */
    small: {
      type: Boolean,
      default: true
    },
    /** Show icon before status text */
    showIcon: {
      type: Boolean,
      default: true
    },
    /** Custom aria-label for accessibility */
    ariaLabel: {
      type: String,
      default: ''
    },
    /** Override icon */
    icon: {
      type: String,
      default: ''
    }
  },

  computed: {
    config(): StatusConfig {
      return statusConfig[this.type]
    },

    computedIcon(): string {
      return this.icon || this.config.icon
    }
  }
})
</script>

<style scoped>
.status-badge {
  font-weight: 500;
  font-size: 0.75rem;
  border-radius: 16px !important;
}

/* Override Vuetify's v-chip colours with design token CSS variables for dark mode support */
.status-badge--success {
  background-color: var(--success-light) !important;
  color: var(--success) !important;
  border: none;
}

.status-badge--success >>> .v-icon {
  color: var(--success) !important;
}

.status-badge--error {
  background-color: var(--danger-light) !important;
  color: var(--danger) !important;
  border: none;
}

.status-badge--error >>> .v-icon {
  color: var(--danger) !important;
}

.status-badge--warning {
  background-color: var(--warning-light) !important;
  color: var(--warning) !important;
  border: none;
}

.status-badge--warning >>> .v-icon {
  color: var(--warning) !important;
}

.status-badge--info {
  background-color: var(--info-light) !important;
  color: var(--info) !important;
  border: none;
}

.status-badge--info >>> .v-icon {
  color: var(--info) !important;
}
</style>
